import { SeededRandom } from "../core/random";
import type { GameHud, GameInstance, GameServices } from "../core/types";
import { clamp, drawPanel, drawScanlines, roundedRectPath, setText } from "./drawing";

const ROUND_SECONDS = 60;
const PLAYER_X = 300;
const TOP = 120;
const BOTTOM = 660;

type ObjectKind = "gate" | "congestion";

interface FieldObject {
  x: number;
  y: number;
  kind: ObjectKind;
  scored: boolean;
}

export class PacketPilotGame implements GameInstance {
  private readonly random = new SeededRandom(Date.now() ^ 0x9a70);
  private readonly objects: FieldObject[] = [];
  private playerY = 390;
  private velocityY = 0;
  private score = 0;
  private time = ROUND_SECONDS;
  private elapsed = 0;
  private spawnTimer = 0.6;
  private scroll = 0;
  private integrity = 4;
  private boostEnergy = 1;
  private boosting = false;
  private shake = 0;
  private message = "Fly through healthy routes, dodge congestion";
  private messageTimer = 0;
  private ended = false;

  constructor(private readonly services: GameServices) {}

  advanceTime(deltaSeconds: number): void {
    if (this.ended) return;
    this.elapsed += deltaSeconds;
    this.time = Math.max(0, ROUND_SECONDS - this.elapsed);
  }

  update(deltaSeconds: number): void {
    if (this.ended) return;

    this.messageTimer = Math.max(0, this.messageTimer - deltaSeconds);
    this.shake = Math.max(0, this.shake - deltaSeconds);
    if (this.messageTimer === 0) this.message = "Fly through healthy routes, dodge congestion";

    if (this.time === 0) {
      this.finish("Route flown clean");
      return;
    }

    this.updateFlight(deltaSeconds);
    this.updateField(deltaSeconds);
  }

  render(context: CanvasRenderingContext2D): void {
    context.save();
    if (this.shake > 0 && !this.services.reducedMotion) {
      context.translate(Math.sin(this.elapsed * 88) * 6, Math.cos(this.elapsed * 66) * 4);
    }
    this.drawSky(context);
    for (const object of this.objects) this.drawObject(context, object);
    this.drawCraft(context);
    context.restore();
    this.drawTelemetry(context);
    this.drawFooter(context);
    drawScanlines(context, "rgba(255, 255, 255, 0.018)");
  }

  getHud(): GameHud {
    return { score: Math.floor(this.score), time: this.time, message: this.message, ended: this.ended };
  }

  destroy(): void {}

  private updateFlight(deltaSeconds: number): void {
    const input = this.services.input;
    const direction = Number(input.isDown("down")) - Number(input.isDown("up"));
    const target = direction * 300;
    this.velocityY += (target - this.velocityY) * Math.min(1, deltaSeconds * 10);
    if (direction === 0) this.velocityY *= Math.max(0, 1 - deltaSeconds * 6);
    this.playerY = clamp(this.playerY + this.velocityY * deltaSeconds, TOP, BOTTOM);

    this.boosting = input.isDown("primary") && this.boostEnergy > 0.02;
    if (this.boosting) {
      this.boostEnergy = Math.max(0, this.boostEnergy - deltaSeconds * 0.36);
    } else {
      this.boostEnergy = Math.min(1, this.boostEnergy + deltaSeconds * 0.13);
    }
  }

  private updateField(deltaSeconds: number): void {
    const speed = this.boosting ? 640 : 440;
    this.scroll += speed * deltaSeconds;
    this.score += (this.boosting ? 70 : 48) * deltaSeconds;
    this.spawnTimer -= deltaSeconds;

    if (this.spawnTimer <= 0 && this.objects.length < 10) {
      this.spawnObject();
      this.spawnTimer = Math.max(0.42, 0.85 - this.elapsed * 0.005) + this.random.between(0, 0.25);
    }

    for (let index = this.objects.length - 1; index >= 0; index -= 1) {
      const object = this.objects[index];
      if (!object) continue;
      object.x -= speed * deltaSeconds;

      if (!object.scored && object.x <= PLAYER_X) {
        object.scored = true;
        this.resolveObject(object);
        if (this.ended) return;
      }
      if (object.x < -80) this.objects.splice(index, 1);
    }
  }

  private spawnObject(): void {
    const kind: ObjectKind = this.random.next() < 0.5 ? "gate" : "congestion";
    this.objects.push({
      x: 1340,
      y: this.random.between(TOP + 30, BOTTOM - 30),
      kind,
      scored: false,
    });
  }

  private resolveObject(object: FieldObject): void {
    const gap = Math.abs(object.y - this.playerY);
    if (object.kind === "gate") {
      if (gap < 60) {
        this.score += this.boosting ? 180 : 130;
        this.services.audio.play("hit");
        this.setMessage(this.boosting ? "Route locked - boost bonus" : "Healthy route locked", 1.3);
      } else {
        this.setMessage("Route gate missed", 1);
      }
      return;
    }
    if (gap < 52) {
      this.integrity -= 1;
      this.score = Math.max(0, this.score - 90);
      this.shake = 0.36;
      this.services.audio.play("miss");
      this.setMessage(`Congestion hit - ${this.integrity} integrity left`, 1.7);
      if (this.integrity <= 0) this.finish("Route saturated - link down");
    }
  }

  private setMessage(message: string, duration: number): void {
    this.message = message;
    this.messageTimer = duration;
    this.services.announce(message);
  }

  private finish(message: string): void {
    this.ended = true;
    this.message = message;
    this.services.audio.play("finish");
    this.services.announce(`${message}. Final score ${Math.floor(this.score)}.`);
  }

  private drawSky(context: CanvasRenderingContext2D): void {
    const sky = context.createLinearGradient(0, 0, 0, 720);
    sky.addColorStop(0, "#071426");
    sky.addColorStop(0.55, "#0b2540");
    sky.addColorStop(1, "#060d18");
    context.fillStyle = sky;
    context.fillRect(0, 0, 1280, 720);

    context.strokeStyle = "rgba(120, 160, 210, 0.14)";
    context.lineWidth = 2;
    for (let index = 0; index < 12; index += 1) {
      const x = ((index * 130 - this.scroll * 0.4) % 1400 + 1400) % 1400 - 60;
      context.beginPath();
      context.moveTo(x, TOP);
      context.lineTo(x, BOTTOM);
      context.stroke();
    }
    context.strokeStyle = "rgba(47, 227, 255, 0.35)";
    context.lineWidth = 2;
    context.strokeRect(60, TOP, 1160, BOTTOM - TOP);
  }

  private drawObject(context: CanvasRenderingContext2D, object: FieldObject): void {
    const gate = object.kind === "gate";
    const accent = gate ? "#2fe3ff" : "#ff5d5d";
    context.save();
    context.translate(object.x, object.y);
    context.shadowColor = accent;
    context.shadowBlur = 18;
    context.strokeStyle = accent;
    context.fillStyle = "rgba(7, 13, 22, 0.85)";
    context.lineWidth = 5;
    if (gate) {
      context.beginPath();
      context.arc(0, 0, 52, 0, Math.PI * 2);
      context.stroke();
      context.beginPath();
      context.arc(0, 0, 34, 0, Math.PI * 2);
      context.stroke();
    } else {
      roundedRectPath(context, -34, -46, 68, 92, 12);
      context.fill();
      context.stroke();
      context.shadowBlur = 0;
      context.strokeStyle = "#f7fbff";
      context.lineWidth = 3;
      context.beginPath();
      context.moveTo(-16, -20);
      context.lineTo(16, 20);
      context.moveTo(16, -20);
      context.lineTo(-16, 20);
      context.stroke();
    }
    context.shadowBlur = 0;
    setText(context, 11, 900, accent, "center");
    context.fillText(gate ? "ROUTE" : "CONGEST", 0, 70);
    context.restore();
  }

  private drawCraft(context: CanvasRenderingContext2D): void {
    context.save();
    context.translate(PLAYER_X, this.playerY);
    context.rotate(clamp(this.velocityY / 900, -0.4, 0.4));
    context.shadowColor = "#2fe3ff";
    context.shadowBlur = this.boosting ? 34 : 18;
    context.fillStyle = "#0b2238";
    context.strokeStyle = "#2fe3ff";
    context.lineWidth = 5;
    context.beginPath();
    context.moveTo(42, 0);
    context.lineTo(-28, -24);
    context.lineTo(-14, 0);
    context.lineTo(-28, 24);
    context.closePath();
    context.fill();
    context.stroke();
    context.shadowBlur = 0;
    if (this.boosting) {
      context.fillStyle = "#ff7a1a";
      context.beginPath();
      context.moveTo(-28, -12);
      context.lineTo(-64, 0);
      context.lineTo(-28, 12);
      context.closePath();
      context.fill();
    }
    context.restore();
  }

  private drawTelemetry(context: CanvasRenderingContext2D): void {
    drawPanel(context, 42, 30, 330, 82, "#ff7a1a");
    setText(context, 12, 850, "#a8f2ff");
    context.fillText("MAGIC WAN LINK", 72, 58);
    setText(context, 22, 900, "#f7fbff");
    context.fillText(this.boosting ? "PRIORITY PATH" : "STEADY PATH", 72, 90);

    setText(context, 11, 850, "#839aa5", "right");
    context.fillText("BOOST", 1232, 44);
    context.fillStyle = "rgba(255, 255, 255, 0.12)";
    roundedRectPath(context, 1012, 58, 220, 12, 6);
    context.fill();
    context.fillStyle = this.boosting ? "#ff7a1a" : "#2fe3ff";
    roundedRectPath(context, 1012, 58, Math.max(3, 220 * this.boostEnergy), 12, 6);
    context.fill();

    setText(context, 11, 850, "#839aa5", "right");
    context.fillText("LINK INTEGRITY", 1232, 84);
    for (let index = 0; index < 4; index += 1) {
      const x = 1150 + index * 24;
      context.fillStyle = index < this.integrity ? "#2fe3ff" : "#485362";
      context.beginPath();
      context.arc(x, 100, 8, 0, Math.PI * 2);
      context.fill();
    }
  }

  private drawFooter(context: CanvasRenderingContext2D): void {
    drawPanel(context, 42, 650, 1196, 54, "#ff7a1a");
    setText(context, 20, 850, "#f7fbff");
    context.fillText(this.message, 72, 678);
    setText(context, 12, 750, "#9cb2bd", "right");
    context.fillText("FLY  \u2191 \u2193 / WASD     BOOST  SPACE", 1204, 678);
  }
}
